import { useParams } from 'react-router-dom';

import { CoordinationActivityTimeline } from '@/modules/coordination/components/CoordinationActivityTimeline';
import { CoordinationControlPanel } from '@/modules/coordination/components/CoordinationControlPanel';
import { CoordinationMeetingsPanel } from '@/modules/coordination/components/CoordinationMeetingsPanel';
import { useApproveCoordinationActionMutation } from '@/modules/coordination/mutations/useApproveCoordinationActionMutation';
import { useRejectCoordinationActionMutation } from '@/modules/coordination/mutations/useRejectCoordinationActionMutation';
import { useCoordinationProcessDetailQuery } from '@/modules/coordination/queries/useCoordinationProcessDetailQuery';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import Container from '@/modules/ui/components/container/Container';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

const CoordinationProcessDetail = () => {
  const { jobId, coordinationProcessId } = useParams<{
    jobId: string;
    coordinationProcessId: string;
  }>();

  const processDetailQuery = useCoordinationProcessDetailQuery(
    jobId,
    coordinationProcessId,
  );
  const approveMutation = useApproveCoordinationActionMutation(
    jobId!,
    coordinationProcessId!,
  );
  const rejectMutation = useRejectCoordinationActionMutation(
    jobId!,
    coordinationProcessId!,
  );

  const process = processDetailQuery.data;
  const candidateName = process
    ? `${process.candidate.firstName} ${process.candidate.lastName}`
    : undefined;

  const isPending = processDetailQuery.isPending;

  return (
    <>
      <DocumentMetadata
        title={
          candidateName
            ? `Coordination for candidate ${candidateName}`
            : 'Coordination'
        }
      />
      <Container>
        <PageHeader
          title={
            isPending ? (
              <Skeleton className="h-8 w-48" />
            ) : candidateName ? (
              `Coordination for candidate ${candidateName}`
            ) : (
              'Coordination'
            )
          }
          action={
            !isPending &&
            process && (
              <CoordinationControlPanel
                jobId={jobId!}
                coordinationProcessId={coordinationProcessId!}
                status={process.status}
                statusHistories={process.statusHistories}
                events={process.data.events}
                isCoordinationActionAutoApproved={
                  process.isCoordinationActionAutoApproved
                }
              />
            )
          }
        />

        {isPending ? (
          <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
            <CoordinationActivityTimeline
              actions={process?.data.actions ?? []}
              events={process?.data.events ?? []}
              onApprove={(coordinationActionId) =>
                approveMutation.mutate({ coordinationActionId })
              }
              onReject={(coordinationActionId) =>
                rejectMutation.mutate({ coordinationActionId })
              }
              isUpdatingAction={
                approveMutation.isPending || rejectMutation.isPending
              }
            />
            <CoordinationMeetingsPanel
              meetings={process?.data.meetings ?? []}
            />
          </div>
        )}
      </Container>
    </>
  );
};

export default CoordinationProcessDetail;
